import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";

interface AuthLandingViewProps {
  busy: boolean;
  error: string | null;
  onGoogleSuccess: (credential: string) => void;
  onApple: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onLogin: () => void;
  onSignup: () => void;
  onClose: () => void;
}

export default function AuthLandingView({
  busy,
  error,
  onGoogleSuccess,
  onApple,
  onLogin,
  onSignup,
  onClose,
}: AuthLandingViewProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className="join-card">
      <button
        className="join-close"
        type="button"
        onClick={onClose}
        aria-label={t("common.close", "Sluiten")}
      >
        ×
      </button>

      <span className="join-badge">
        {t("auth.protectedPage", "Beveiligde pagina")}
      </span>

      <h2 className="join-title">
        {t("auth.loginToView", "Log in om je favorieten te bekijken")}
      </h2>

      <p className="join-subtitle">
        {t(
          "auth.loginSubtitle",
          "Bewaar shirts, volg veilingen en beheer alles veilig vanuit je account.",
        )}
      </p>

      <div className="join-buttons">
        {/* Google knop: custom styled knop als overlay over de echte GoogleLogin */}
        <div className="join-oauth-google-container">
          <div className="join-oauth-google-real">
            <GoogleLogin
              onSuccess={(response) => {
                if (response.credential) {
                  onGoogleSuccess(response.credential);
                }
              }}
              onError={() => console.error("Google Login Failed")}
              useOneTap={false}
              shape="rectangular"
              theme="outline"
              text="continue_with"
              size="large"
              width="420"
            />
          </div>
          <div className="join-oauth join-oauth--google join-oauth-google-overlay" aria-hidden="true">
            <svg className="join-icon" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            {t("auth.continueGoogle", "Doorgaan met Google")}
          </div>
        </div>

        <button
          className="join-oauth join-oauth--apple"
          type="button"
          onClick={onApple}
          disabled={busy}
        >
          <svg
            className="join-icon join-icon--apple"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path d="M16.365 1.43c0 1.14-.41 2.2-1.22 3.13-.98 1.12-2.6 1.98-4.07 1.86-.18-1.47.52-2.96 1.36-3.92.93-1.07 2.59-1.85 3.93-2.07.02.33.0.67 0 1z" />
            <path d="M20.77 17.04c-.55 1.23-.82 1.78-1.53 2.87-.98 1.49-2.36 3.35-4.07 3.36-1.52.02-1.91-.99-3.98-1-2.07-.01-2.5 1.02-4.01 1-1.7-.01-3.0-1.68-3.98-3.17-2.74-4.2-3.03-9.12-1.34-11.72 1.2-1.85 3.1-2.94 4.88-2.94 1.82 0 2.97 1.01 4.48 1.01 1.47 0 2.36-1.02 4.46-1.02 1.58 0 3.25.86 4.45 2.35-3.92 2.15-3.29 7.73.64 9.26z" />
          </svg>
          {t("auth.continueApple", "Doorgaan met Apple")}
        </button>
      </div>

      <div className="join-or">
        <span className="join-or-line" />
        <span className="join-or-text">{t("auth.or", "of")}</span>
        <span className="join-or-line" />
      </div>

      {error && (
        <div className="join-error">{error}</div>
      )}

      <div className="join-actions">
        <button
          className="join-email-btn"
          type="button"
          onClick={onLogin}
          disabled={busy}
        >
          {t("auth.loginWithEmail", "Inloggen met e-mail")}
        </button>

        <button
          className="join-signup-btn"
          type="button"
          onClick={onSignup}
          disabled={busy}
        >
          {t("auth.createAccount", "Account aanmaken")}
        </button>
      </div>

      <p className="join-legal">
        {t("auth.legalPrefix", "Door verder te gaan ga je akkoord met de")}{" "}
        <a href="/terms" onClick={(e) => { e.preventDefault(); onClose(); navigate("/info"); }}>
          {t("auth.terms", "Algemene Voorwaarden")}
        </a>{" "}
        {t("auth.legalAnd", "en")}{" "}
        <a href="/privacy" onClick={(e) => { e.preventDefault(); onClose(); navigate("/info"); }}>
          {t("auth.privacy", "Privacyverklaring")}
        </a>
        .
      </p>

      <button
        className="join-back-link"
        type="button"
        onClick={onClose}
      >
        {t("auth.backToPrevious", "Terug naar vorige pagina")}
      </button>
    </div>
  );
}
